import { createElement } from 'react';
import { GridColDef, GridDensity } from '@mui/x-data-grid-pro';
import { SettlementLoadRecord } from '@api/Trinium/Endpoints/Settlements/types';
import { ParsedDate } from '@api/Trinium/types';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import DensityMediumIcon from '@mui/icons-material/DensityMedium';
import DensityLargeIcon from '@mui/icons-material/DensityLarge';
import { LocationCell } from './LocationCell';
import { negativeAmountClass } from '../gridStyles';

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
});

export function formatLocation(name: string, city: string, state: string) {
  const cityState = [city, state].filter(Boolean).join(', ');
  return [name, cityState].filter(Boolean).join(' — ');
}

export const DENSITY_OPTIONS: { value: GridDensity; label: string; icon: typeof DensitySmallIcon }[] = [
  { value: 'compact', label: 'Compact', icon: DensitySmallIcon },
  { value: 'standard', label: 'Standard', icon: DensityMediumIcon },
  { value: 'comfortable', label: 'Comfortable', icon: DensityLargeIcon }
];

export const columns: GridColDef<SettlementLoadRecord>[] = [
  { field: 'loadNo', headerName: 'Order #', width: 100 },
  {
    field: 'costDate',
    headerName: 'Cost Date',
    width: 120,
    valueGetter: (value: ParsedDate | undefined) => value?.shortDateExtended,
    sortComparator: (a: string | undefined, b: string | undefined) => (a ? new Date(a).getTime() : 0) - (b ? new Date(b).getTime() : 0)
  },
  { field: 'serviceCode', headerName: 'Code', width: 100 },
  { field: 'serviceDescription', headerName: 'Service', minWidth: 180, flex: 1 },
  {
    field: 'commission',
    headerName: 'Commission',
    width: 110,
    type: 'number',
    valueFormatter: (value: number | undefined) => (value != null ? currencyFormatter.format(value) : ''),
    cellClassName: ({ row }) => (row.commission < 0 ? negativeAmountClass : '')
  },
  { field: 'container', headerName: 'Container', width: 120 },
  { field: 'chassis', headerName: 'Chassis', width: 100 },
  {
    field: 'pickup',
    headerName: 'Pickup',
    minWidth: 120,
    flex: 1,
    display: 'flex',
    valueGetter: (_value, row) => formatLocation(row.puName, row.puCity, row.puState),
    renderCell: ({ row }) =>
      createElement(LocationCell, {
        name: row.puName,
        city: row.puCity,
        state: row.puState
      })
  },
  {
    field: 'delivery',
    headerName: 'Delivery',
    minWidth: 120,
    flex: 1,
    display: 'flex',
    valueGetter: (_value, row) => formatLocation(row.dlvName, row.dlvCity, row.dlvState),
    renderCell: ({ row }) =>
      createElement(LocationCell, {
        name: row.dlvName,
        city: row.dlvCity,
        state: row.dlvState
      })
  },
  {
    field: 'customer',
    headerName: 'Customer',
    minWidth: 120,
    flex: 1,
    display: 'flex',
    valueGetter: (_value, row) => formatLocation(row.custName, row.custCity, row.custState),
    renderCell: ({ row }) =>
      createElement(LocationCell, {
        name: row.custName,
        city: row.custCity,
        state: row.custState
      })
  },
  { field: 'notes', headerName: 'Notes', minWidth: 220, flex: 1.5 }
];
